import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { EyeIcon, EyeOff } from 'lucide-react';

import { Button } from '@/slw/shared/ui/button';
import { PasswordStrengthBar } from '@/slw/shared/ui/password-strength';
import { PasswordRequirements } from '@/slw/shared/ui/password-requirements';
import { Label } from '@/slw/shared/ui/label';
import FormProvider, { Checkbox, Input } from '@/slw/shared/ui/form';
import { getPasswordStrength } from '@/slw/shared/lib/password-strength';
import { RegisterFormValues } from '@/slw/entities/auth/auth.type';
import { registerSchema } from '@/slw/entities/auth/auth.schema';
import { CheckCircle } from 'lucide-react';

export function RegisterStep({
  email,
  onSubmit,
  loading,
}: {
  email: string;
  onSubmit: (data: RegisterFormValues) => void;
  loading: boolean;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const methods = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { firstName: '', lastName: '', password: '', confirmPassword: '', terms: false },
  });
  const password = methods.watch('password') || '';
  const confirmPassword = methods.watch('confirmPassword') || '';

  const strength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword;

  return (
    <FormProvider {...methods} onSubmit={(v) => onSubmit(v)} className='w-full flex flex-col justify-center'>
      <p className='font-normal text-sm mb-[2rem]'>Your email address: {email}</p>
      <div className='flex gap-4 mb-4'>
        <Input direction='column' containerClass='w-full' label='First Name' name='firstName' required />
        <Input direction='column' containerClass='w-full' label='Last Name' name='lastName' required />
      </div>
      
      {/* Password Field */}
      <div className='flex flex-col gap-2 mb-4'>
        <Label htmlFor='password' className='whitespace-nowrap font-normal text-sm'>
          Your password<span className='text-[#dc2626]'>*</span>
        </Label>
        <div className='relative'>
          <Input
            direction='column'
            id='password'
            name='password'
            type={showPassword ? 'text' : 'password'}
            placeholder='Enter your password'
            helperText='Choose a strong password that meets all requirements below.'
          />
          <button
            type='button'
            className='absolute right-4 top-1/2 -translate-y-[calc(50%+12px)]'
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeIcon className='w-5 h-5 text-default-400' /> : <EyeOff className='w-5 h-5 text-default-400' />}
          </button>
        </div>
        {password && <PasswordStrengthBar password={password} />}
        <PasswordRequirements password={password} />
      </div>

      {/* Confirm Password Field */}
      <div className='flex flex-col gap-2 mb-6'>
        <Label htmlFor='confirmPassword' className='whitespace-nowrap font-normal text-sm'>
          Confirm password<span className='text-[#dc2626]'>*</span>
        </Label>
        <div className='relative'>
          <Input
            direction='column'
            id='confirmPassword'
            name='confirmPassword'
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder='Confirm your password'
            helperText={confirmPassword && !passwordsMatch ? 'Passwords do not match' : ''}
          />
          <button
            type='button'
            className='absolute right-4 top-1/2 -translate-y-[calc(50%+12px)]'
            tabIndex={-1}
            onClick={() => setShowConfirmPassword((v) => !v)}
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
          >
            {showConfirmPassword ? <EyeIcon className='w-5 h-5 text-default-400' /> : <EyeOff className='w-5 h-5 text-default-400' />}
          </button>
        </div>
        {confirmPassword && passwordsMatch && (
          <div className='text-xs text-green-600 flex items-center gap-1'>
            <CheckCircle className='w-3 h-3' />
            Passwords match
          </div>
        )}
      </div>

      <div className='flex items-center my-8 gap-2'>
        <Checkbox color='green' id='terms' name='terms' withError={false} />
        <label htmlFor='terms' className='text-sm'>
          I have read and agreed to Clovis Classics&apos;{' '}
          <a href='/privacy' className='font-medium text-primary'>
            Privacy Policy
          </a>{' '}
          &{' '}
          <a href='/terms-and-conditions' className='font-medium text-primary'>
            Terms and Conditions
          </a>
        </label>
      </div>
      <Button type='submit' disabled={loading || !methods.formState.isValid || strength === 'weak' || !passwordsMatch}>
        {loading ? 'Loading...' : 'Continue'}
      </Button>
    </FormProvider>
  );
}
